import { useCallback, useEffect, useMemo, useState } from 'react';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Box, Button, Link, Skeleton, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router';

import PremiumDialog from '@/components/UpgradeToPremium/PremiumDialog';
import { useAppSelector } from '@/hooks/redux-hooks';
import useUserDetails from '@/hooks/useUserDetails';
import { getProducts } from '@/pages/Subscription/api/getProducts';
import ProductTile from '@/pages/Subscription/components/ProductTile';
import { Product } from '@/pages/Subscription/types/ManageSubscription';
import generateSkeletonKeys from '@/utils/generateSkeletonKeys';

import shouldShowContactAction from './utils/shouldShowContactAction';
import { FACTSHEET_PARAM_NAME } from '../Facthsheets/constants';

const isStripeEnabled = import.meta.env.VITE_STRIPE_ENABLED === 'true';

const skeletonKeys = generateSkeletonKeys(1);
const ManageSubscription = () => {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const handleBack = () => {
    const canGoBack = window.history.length > 1;
    if (canGoBack) {
      navigate(-1);
    } else {
      navigate('/'); // Navigate to the main page if there's no history
    }
  };

  useEffect(() => {
    setSearchParams(searchParams => {
      searchParams.delete(FACTSHEET_PARAM_NAME);
      return searchParams;
    });
  }, [setSearchParams]);

  const { data: products, isLoading } = useQuery({
    queryKey: ['pricing'],
    queryFn: getProducts
  });

  const { userEmail } = useAppSelector(state => state.auth);
  const {
    isUserExpired,
    isUserPremium,
    formatedExpirationDate,
    currentSubscription,
    salesChannel,
    isTrial
  } = useUserDetails();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'premium' | 'enterprise' | undefined>(undefined);

  const isPlanButtonDisabled = useCallback(
    (planName: string) => {
      if (isTrial || isUserExpired) return false;

      if (isUserPremium) {
        return planName.toLowerCase() !== 'premium';
      }
      return false;
    },
    [isTrial, isUserExpired, isUserPremium]
  );

  const isContactAction = useMemo(() => {
    return shouldShowContactAction(isUserExpired, salesChannel, isTrial, isStripeEnabled);
  }, [isUserExpired, salesChannel, isTrial]);

  const handleProductClick = useCallback(
    (product: Product) => {
      if (isContactAction) {
        setDialogType(product.name.toLowerCase() === 'enterprise' ? 'enterprise' : 'premium');
        setDialogOpen(true);
        return;
      }
      const url = new URL(product.metadata.payment_link);
      if (userEmail) {
        const params = new URLSearchParams({
          __customer_email: userEmail,
          prefilled_email: userEmail
        });
        url.search = params.toString();
      }
      window.open(url.toString(), '_blank', 'noopener,noreferrer');
    },
    [isContactAction, userEmail]
  );

  return (
    <Box sx={{ pt: 3 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div>
          <Button
            sx={{ p: 0, minWidth: 'auto' }}
            variant='text'
            onClick={handleBack}
          >
            <ChevronLeftIcon />
            Back
          </Button>
        </div>
        <Typography
          sx={{ fontSize: '32px', fontWeight: '700' }}
          variant='h1'
        >
          Your Account
        </Typography>
        {currentSubscription && (
          <Typography>
            <span style={{ fontWeight: 700 }}>Current subscription:</span> {currentSubscription}
          </Typography>
        )}
        {formatedExpirationDate && (
          <Typography>
            <span style={{ fontWeight: 700 }}>Expiry date:</span> {formatedExpirationDate}
          </Typography>
        )}
        <Typography
          sx={{ mt: '12px', mb: '24px', fontWeight: 700, fontSize: '20px' }}
          variant='h6'
        >
          Pick the plan that works for you
        </Typography>
        <Box
          sx={{
            display: 'flex',
            px: '32px',
            gap: '32px',
            justifyContent: 'center'
          }}
        >
          {isLoading &&
            skeletonKeys.map(key => (
              <Skeleton
                key={key}
                height={600}
                sx={{ flex: 1 }}
                variant='rectangular'
              />
            ))}
          {!isLoading &&
            products?.map((product, index) => (
              <ProductTile
                key={product.id}
                btnLabel={isContactAction ? 'Get in touch' : 'Buy Now'}
                currency={product.currency}
                disabled={isPlanButtonDisabled(product.name)}
                features={product.marketingFeatures}
                featuresHeader='All of the free trial plan and:'
                icon={product.images?.[0]}
                isFirstFeatureFromPreviousPlan={index > 0}
                paymentInterval='per year'
                price={product.defaultPrice}
                priceAddon='(includes 3 users)'
                shortDescription={product.description}
                title={product.name}
                variant={product.metadata.display_variant}
                onClick={() => {
                  handleProductClick(product);
                }}
              />
            ))}
          {!isLoading && (
            <ProductTile
              btnLabel='Get in touch'
              features={['Need broader enterprise access for your team?']}
              icon='/icons/enterprise.jpg'
              price='Let’s talk!'
              shortDescription='Analyze, chart, download and integrate with your systems using our Enterprise API.'
              title='Enterprise'
              variant='combined'
              onClick={() => {
                setDialogType('enterprise');
                setDialogOpen(true);
              }}
            />
          )}
        </Box>
      </Box>
      <PremiumDialog
        open={dialogOpen}
        type={dialogType}
        onClose={() => {
          setDialogOpen(false);
        }}
      />
      <Typography sx={{ mt: '32px', fontSize: '20px' }}>
        Any Questions?{' '}
        <Link
          href='https://www.general-index.com/contact-us'
          target='_blank'
          underline='hover'
        >
          Contact Us
        </Link>
      </Typography>
    </Box>
  );
};

export default ManageSubscription;
